import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ContactsApi, type UpsertContactInput } from "@/api/contacts/ContactsApi";
import { queryKeys } from "@/api/queryKeys";
import {
  FINDER_LINKED_UPDATE_FAILED,
  FINDER_LINKED_UPDATE_OFFLINE,
  FINDER_MATCH_SAFETY_DETACHED,
  FINDER_PARTIAL_IDENTITY_LOCKED,
  FINDER_PARTIAL_IDENTITY_RESTORED,
  finderLinkedUpdateFailedNotice,
  finderMatchBaselineDeviates,
  finderReuseUnchanged,
  type PrivacyProvisionKind,
} from "@/ui/finder/finderMode";
import { isNetworkError, uiErrorMessage } from "@/utils/isoDate";

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
}

function isFinderPiiRequired(error: unknown): boolean {
  return errorText(error).includes("finder_pii required");
}

function asksToRetry(text: string): boolean {
  return /erneut versuchen/i.test(text);
}

export type FinderRestoreContact = {
  clientContactId: string;
  displayName: string;
  phone: string;
  email: string;
  address: string;
  notes: string;
  preferredChannel: string;
  privacyNoticeVersion: string;
  privacyProvisionKind: PrivacyProvisionKind | null;
  privacyDocumented: boolean;
  matchedContactId: string | null;
};

export type PersistFinderResult = {
  id: string;
  notice: string | null;
  identityLocked?: boolean;
  restoreContact?: FinderRestoreContact;
};

function personSnapshot(contact: UpsertContactInput, clientContactId: string): FinderRestoreContact {
  return {
    clientContactId,
    displayName: contact.displayName ?? "",
    phone: contact.phone ?? "",
    email: contact.email ?? "",
    address: contact.address ?? "",
    notes: contact.notes ?? "",
    preferredChannel: contact.preferredChannel ?? "",
    privacyNoticeVersion: contact.privacyNoticeVersion ?? "",
    privacyProvisionKind: contact.privacyProvisionKind ?? null,
    privacyDocumented: contact.privacyDocumented === true,
    matchedContactId: contact.matchedContactId ?? null,
  };
}

/**
 * Nach gelungenem Link: gleicher Hinweis.
 * Ein Netzfehler ersetzt den Satz. Andere deutsche Texte nur ohne Aufforderung zum erneuten Versuch.
 */
function linkedUpdateNotice(error: unknown): string {
  if (isFinderPiiRequired(error)) return FINDER_LINKED_UPDATE_FAILED;
  if (isNetworkError(error)) return FINDER_LINKED_UPDATE_OFFLINE;
  const detail = uiErrorMessage(error, "").trim();
  if (!detail || asksToRetry(detail)) return FINDER_LINKED_UPDATE_FAILED;
  return finderLinkedUpdateFailedNotice(detail);
}

export function useCaseFinderView(caseId: string | undefined) {
  const q = useQuery({
    queryKey: queryKeys.contacts.caseFinder(caseId ?? ""),
    queryFn: () => ContactsApi.getCaseFinderView(caseId!),
    enabled: !!caseId,
  });
  return {
    view: q.data ?? null,
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

function partialLinkedContact(linked: Set<string>, caseIds: string[]): string | null {
  const counts = new Map<string, number>();
  for (const key of linked) {
    const sep = key.indexOf("\0");
    if (sep < 0) continue;
    const caseId = key.slice(0, sep);
    const serverId = key.slice(sep + 1);
    if (!caseIds.includes(caseId) || !serverId) continue;
    counts.set(serverId, (counts.get(serverId) ?? 0) + 1);
  }
  for (const [serverId, count] of counts) {
    if (count > 0 && count < caseIds.length) return serverId;
  }
  return null;
}

export function usePersistFinderForCases() {
  const qc = useQueryClient();
  const linkedKeysRef = useRef(new Set<string>());
  const createdIdsRef = useRef(new Map<string, string>());
  const linkedPeopleRef = useRef(new Map<string, FinderRestoreContact>());
  return useMutation({
    mutationFn: async (input: {
      organizationId: string;
      caseIds: string[];
      contact: UpsertContactInput;
    }): Promise<PersistFinderResult> => {
      const reuseId = input.contact.matchedContactId ?? input.contact.id ?? null;
      const createdKey = input.contact.clientContactId?.trim() ?? "";
      const rememberedId = createdKey ? createdIdsRef.current.get(createdKey) ?? null : null;
      const partialId = partialLinkedContact(linkedKeysRef.current, input.caseIds);
      const targetId = reuseId ?? rememberedId;
      if (partialId && targetId !== partialId) {
        const restoreContact = linkedPeopleRef.current.get(partialId);
        return {
          id: partialId,
          notice: restoreContact
            ? FINDER_PARTIAL_IDENTITY_RESTORED
            : FINDER_PARTIAL_IDENTITY_LOCKED,
          identityLocked: true,
          restoreContact,
        };
      }
      const matchedId = input.contact.matchedContactId ?? null;
      if (
        matchedId &&
        finderMatchBaselineDeviates(input.contact, input.contact.reuseBaseline)
      ) {
        const detachedContact = {
          ...input.contact,
          id: null,
          matchedContactId: null,
        };
        const retryId = rememberedId && rememberedId !== matchedId ? rememberedId : null;
        let contactId = retryId;
        let notice: string | null = FINDER_MATCH_SAFETY_DETACHED;
        if (!contactId) {
          const created = await ContactsApi.upsertContact({
            ...detachedContact,
            organizationId: input.organizationId,
          });
          contactId = created.id;
          if (createdKey) createdIdsRef.current.set(createdKey, contactId);
        }
        linkedPeopleRef.current.set(contactId, personSnapshot(detachedContact, createdKey));
        for (const caseId of input.caseIds) {
          const key = `${caseId}\0${contactId}`;
          if (linkedKeysRef.current.has(key)) continue;
          await ContactsApi.linkCaseContact({
            caseId,
            contactId,
            roles: ["finder"],
            isPrimaryFinder: true,
          });
          linkedKeysRef.current.add(key);
        }
        if (retryId) {
          try {
            await ContactsApi.upsertContact({
              ...detachedContact,
              organizationId: input.organizationId,
              id: retryId,
            });
          } catch (error) {
            notice = linkedUpdateNotice(error);
          }
        }
        return { id: contactId, notice };
      }
      let contactId = reuseId;
      let notice: string | null = null;
      const linkAll = async (id: string) => {
        for (const caseId of input.caseIds) {
          const key = `${caseId}\0${id}`;
          if (linkedKeysRef.current.has(key)) continue;
          await ContactsApi.linkCaseContact({
            caseId,
            contactId: id,
            roles: ["finder"],
            isPrimaryFinder: true,
          });
          linkedKeysRef.current.add(key);
        }
      };
      const keepPerson = (id: string) => {
        linkedPeopleRef.current.set(id, personSnapshot(input.contact, createdKey));
      };
      if (!contactId) {
        if (rememberedId) {
          contactId = rememberedId;
          keepPerson(contactId);
          await linkAll(contactId);
          try {
            await ContactsApi.upsertContact({
              ...input.contact,
              organizationId: input.organizationId,
              id: contactId,
            });
          } catch (error) {
            notice = linkedUpdateNotice(error);
          }
        } else {
          const created = await ContactsApi.upsertContact({
            ...input.contact,
            organizationId: input.organizationId,
          });
          contactId = created.id;
          if (createdKey) createdIdsRef.current.set(createdKey, contactId);
          keepPerson(contactId);
          await linkAll(contactId);
        }
      } else {
        keepPerson(contactId);
        await linkAll(contactId);
        if (!finderReuseUnchanged(input.contact, input.contact.reuseBaseline)) {
          try {
            await ContactsApi.upsertContact({
              ...input.contact,
              organizationId: input.organizationId,
              id: contactId,
            });
          } catch (error) {
            notice = linkedUpdateNotice(error);
          }
        }
      }
      if (!contactId) {
        throw new Error("Finder-Kontakt ohne ID");
      }
      return { id: contactId, notice };
    },
    onSuccess: (_data, vars) => {
      for (const caseId of vars.caseIds) {
        void qc.invalidateQueries({
          queryKey: queryKeys.contacts.caseFinder(caseId),
        });
      }
    },
  });
}
